import React, { useEffect, useRef, useState } from 'react'

type RichTextEditorProps = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  maxWords?: number
  className?: string
}

export const getRichTextWordCount = (html: string) => {
  const text = getRichTextPlainText(html)
  return text ? text.split(/\s+/).length : 0
}

export const getRichTextPlainText = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').trim()

const ToolbarButton = ({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    className="min-w-8 h-8 px-2 rounded text-xs font-semibold text-gray-200 hover:bg-dark-600 focus:outline-none focus:ring-1 focus:ring-primary-500"
  >
    {children}
  </button>
)

/** A small, dependency-free document editor that persists standards-compatible HTML. */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder = 'Start writing…', maxWords, className = '' }) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const selectionRef = useRef<Range | null>(null)
  const [wordCount, setWordCount] = useState(() => getRichTextWordCount(value))

  useEffect(() => {
    const editor = editorRef.current
    if (editor && editor.innerHTML !== value) editor.innerHTML = value
    setWordCount(getRichTextWordCount(value))
  }, [value])

  const emitChange = () => {
    const html = editorRef.current?.innerHTML || ''
    setWordCount(getRichTextWordCount(html))
    onChange(html === '<br>' ? '' : html)
  }

  // Toolbar controls sit outside contentEditable. Remembering and restoring the
  // range keeps formatting applied to the text the admin actually selected.
  const saveSelection = () => {
    const editor = editorRef.current
    const selection = window.getSelection()
    if (!editor || !selection?.rangeCount) return
    const range = selection.getRangeAt(0)
    if (editor.contains(range.commonAncestorContainer)) selectionRef.current = range.cloneRange()
  }

  const restoreSelection = () => {
    const selection = window.getSelection()
    const range = selectionRef.current
    if (!selection || !range) return
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const command = (name: string, commandValue?: string) => {
    editorRef.current?.focus()
    restoreSelection()
    document.execCommand(name, false, commandValue)
    saveSelection()
    emitChange()
  }

  const toggleBlockquote = () => {
    const range = selectionRef.current
    const element = range?.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer as Element
      : range?.commonAncestorContainer.parentElement
    const isQuote = element?.closest('blockquote')
    command('formatBlock', isQuote ? '<p>' : '<blockquote>')
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const selection = window.getSelection()
    const node = selection?.anchorNode
    const element = node?.nodeType === Node.ELEMENT_NODE ? node as Element : node?.parentElement
    if (!element?.closest('li')) return
    event.preventDefault()
    command(event.shiftKey ? 'outdent' : 'indent')
  }

  const insertLink = () => {
    const url = window.prompt('Link URL')
    if (url) command('createLink', url)
  }

  const insertImage = () => {
    const url = window.prompt('Image URL')
    if (url) command('insertImage', url)
  }

  const insertTable = () => {
    const rows = Math.max(1, Math.min(10, Number(window.prompt('Number of rows', '2')) || 2))
    const columns = Math.max(1, Math.min(8, Number(window.prompt('Number of columns', '2')) || 2))
    const headers = Array.from({ length: columns }, (_, index) => `<th>Header ${index + 1}</th>`).join('')
    const body = Array.from({ length: rows }, () => `<tr>${Array.from({ length: columns }, () => '<td>&nbsp;</td>').join('')}</tr>`).join('')
    command('insertHTML', `<table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table><p><br></p>`)
  }

  const selectedTableCell = () => {
    const range = selectionRef.current
    const element = range?.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer as Element
      : range?.commonAncestorContainer.parentElement
    return element?.closest('td, th') as HTMLTableCellElement | null
  }

  const updateTable = (operation: 'add-row' | 'delete-row' | 'add-column' | 'delete-column') => {
    editorRef.current?.focus()
    restoreSelection()
    const cell = selectedTableCell()
    const row = cell?.closest('tr') as HTMLTableRowElement | null
    const table = cell?.closest('table') as HTMLTableElement | null
    if (!cell || !row || !table) return

    if (operation === 'add-row') {
      const newRow = row.cloneNode(true) as HTMLTableRowElement
      Array.from(newRow.cells).forEach(newCell => { newCell.innerHTML = '&nbsp;' })
      row.insertAdjacentElement('afterend', newRow)
    } else if (operation === 'delete-row') {
      row.remove()
    } else {
      const cellIndex = Array.from(row.cells).indexOf(cell)
      Array.from(table.rows).forEach(currentRow => {
        if (operation === 'add-column') {
          const newCell = document.createElement(currentRow.parentElement?.tagName === 'THEAD' ? 'th' : 'td')
          newCell.innerHTML = '&nbsp;'
          currentRow.insertBefore(newCell, currentRow.cells[cellIndex] || null)
        } else if (currentRow.cells.length > cellIndex) {
          currentRow.deleteCell(cellIndex)
        }
      })
    }
    saveSelection()
    emitChange()
  }

  const overLimit = maxWords !== undefined && wordCount > maxWords

  return (
    <div className={`overflow-hidden rounded-lg border ${overLimit ? 'border-red-500' : 'border-dark-600'} bg-dark-900 focus-within:ring-1 focus-within:ring-primary-500 ${className}`}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-dark-700 bg-dark-800 p-1.5">
        <select aria-label="Text style" onChange={e => command('formatBlock', e.target.value)} defaultValue="p" className="h-8 max-w-[8.5rem] rounded bg-dark-700 px-2 text-xs text-gray-200 outline-none">
          <option value="p">Paragraph</option>
          {[1, 2, 3, 4, 5, 6].map(level => <option key={level} value={`h${level}`}>Heading {level}</option>)}
        </select>
        <select aria-label="Font size" onChange={e => command('fontSize', e.target.value)} defaultValue="3" className="h-8 rounded bg-dark-700 px-2 text-xs text-gray-200 outline-none">
          <option value="1">Small</option><option value="3">Normal</option><option value="5">Large</option><option value="7">Huge</option>
        </select>
        <span className="mx-1 h-5 border-l border-dark-600" />
        <ToolbarButton label="Bold" onClick={() => command('bold')}><strong>B</strong></ToolbarButton>
        <ToolbarButton label="Italic" onClick={() => command('italic')}><em>I</em></ToolbarButton>
        <ToolbarButton label="Underline" onClick={() => command('underline')}><span className="underline">U</span></ToolbarButton>
        <ToolbarButton label="Strikethrough" onClick={() => command('strikeThrough')}><span className="line-through">S</span></ToolbarButton>
        <label title="Text color" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-dark-600"><span className="font-bold text-gray-200">A</span><input aria-label="Text color" type="color" onChange={e => command('foreColor', e.target.value)} className="sr-only" /></label>
        <label title="Highlight color" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-dark-600"><span className="rounded bg-yellow-300 px-1 text-xs text-black">A</span><input aria-label="Highlight color" type="color" defaultValue="#facc15" onChange={e => command('hiliteColor', e.target.value)} className="sr-only" /></label>
        <span className="mx-1 h-5 border-l border-dark-600" />
        <ToolbarButton label="Align left" onClick={() => command('justifyLeft')}>≡</ToolbarButton>
        <ToolbarButton label="Align center" onClick={() => command('justifyCenter')}>≡</ToolbarButton>
        <ToolbarButton label="Align right" onClick={() => command('justifyRight')}>≡</ToolbarButton>
        <ToolbarButton label="Bulleted list" onClick={() => command('insertUnorderedList')}>• ≡</ToolbarButton>
        <ToolbarButton label="Numbered list" onClick={() => command('insertOrderedList')}>1. ≡</ToolbarButton>
        <ToolbarButton label="Blockquote" onClick={toggleBlockquote}>❝</ToolbarButton>
        <ToolbarButton label="Code block" onClick={() => command('formatBlock', 'pre')}>{'</>'}</ToolbarButton>
        <ToolbarButton label="Table" onClick={insertTable}>▦</ToolbarButton>
        <ToolbarButton label="Add table row" onClick={() => updateTable('add-row')}>+R</ToolbarButton>
        <ToolbarButton label="Delete table row" onClick={() => updateTable('delete-row')}>−R</ToolbarButton>
        <ToolbarButton label="Add table column" onClick={() => updateTable('add-column')}>+C</ToolbarButton>
        <ToolbarButton label="Delete table column" onClick={() => updateTable('delete-column')}>−C</ToolbarButton>
        <ToolbarButton label="Link" onClick={insertLink}>↗</ToolbarButton>
        <ToolbarButton label="Image" onClick={insertImage}>▧</ToolbarButton>
        <ToolbarButton label="Horizontal rule" onClick={() => command('insertHorizontalRule')}>—</ToolbarButton>
        <ToolbarButton label="Undo" onClick={() => command('undo')}>↶</ToolbarButton>
        <ToolbarButton label="Redo" onClick={() => command('redo')}>↷</ToolbarButton>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onKeyDown={handleKeyDown}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onFocus={saveSelection}
        className="rich-text-editor min-h-[18rem] max-w-none overflow-x-auto px-4 py-3 text-sm leading-7 text-gray-100 outline-none empty:before:pointer-events-none empty:before:text-gray-500 empty:before:content-[attr(data-placeholder)] [&_a]:text-primary-400 [&_a]:underline [&_blockquote]:my-3 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-500 [&_blockquote]:pl-4 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol_ol]:list-[lower-alpha] [&_ol_ul]:list-[circle] [&_ul_ol]:list-[lower-alpha] [&_ul_ul]:list-[circle] [&_li]:pl-1 [&_code]:rounded [&_code]:bg-dark-700 [&_code]:px-1 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-black/30 [&_pre]:p-3 [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-dark-600 [&_td]:p-2 [&_th]:border [&_th]:border-dark-600 [&_th]:p-2 [&_img]:my-3 [&_img]:max-w-full [&_hr]:my-5 [&_hr]:border-dark-600"
      />
      {maxWords !== undefined && <div className={`border-t border-dark-700 px-3 py-2 text-right text-xs ${overLimit ? 'text-red-400' : 'text-gray-400'}`}>{wordCount} / {maxWords} words{overLimit ? ' — reduce content before saving' : ''}</div>}
    </div>
  )
}

export default RichTextEditor
